import {marked} from 'marked';
import PlainTextRenderer from 'marked-plaintext';

import type {Project, ProjectDraft, ProjectURL} from '../../types/project';
import {VIEW_PROJECT} from '../../constants/views';

// Use only one whitespace delimiter for line breaks in Google Slides
PlainTextRenderer.prototype.br = function () {
  return this.whitespaceDelimiter;
};

const markdownToPlaintext = (md: string) =>
  marked(md, {renderer: new PlainTextRenderer()}).replaceAll('&quot;', '"');

const getDateFormatForCoverSlide = (date: Date) =>
  date.toLocaleDateString('de-DE', {
    month: 'short',
    year: 'numeric'
  });

const getContentMapping = (project: Project | ProjectDraft) => {
  const projectUrlTemplate = `${window.location.origin}/#${VIEW_PROJECT}`;
  const relatedProjectsUrls = project.relatedProjects?.map(
    (relatedProjectId: string) =>
      projectUrlTemplate.replace(':id', relatedProjectId)
  );

  /**
   * The `replaceAllText` operation of Slides API does not allow newline
   * characters, so two separate placeholders are necessary to have customers
   * and project title on two different lines. Also if there's no customer,
   * the title will start on the first line
   */
  const coverSlide =
    project.customers && project.customers.length > 0
      ? [
          {
            presentationKey: '{{ customers_or_title }}',
            projectProperty: `${project.customers?.join(', ')}:`
          },
          {
            presentationKey: '{{ title_only }}',
            projectProperty: project.title || ''
          }
        ]
      : [
          {
            presentationKey: '{{ customers_or_title }}',
            projectProperty: project.title || ''
          },
          {
            presentationKey: '{{ title_only }}',
            projectProperty: ''
          }
        ];

  return [
    ...coverSlide,
    {
      presentationKey: '{{ title_small }}',
      projectProperty: project.title || ''
    },
    {
      presentationKey: '{{ creationPeriod }}',
      projectProperty: `${
        project.creationPeriod?.start
          ? getDateFormatForCoverSlide(project.creationPeriod.start)
          : ''
      }${
        project.creationPeriod?.end
          ? ` – ${getDateFormatForCoverSlide(project.creationPeriod.end)}`
          : ''
      }`
    },
    {
      presentationKey: '{{ topics }}',
      projectProperty: project.topics?.join(', ') || ''
    },
    {
      presentationKey: '{{ tasks }}',
      projectProperty: ('tasks' in project && project.tasks?.join(', ')) || ''
    },
    {
      presentationKey: '{{ technologies }}',
      projectProperty:
        ('technologies' in project && project.technologies?.join(', ')) || ''
    },
    {
      presentationKey: '{{ fields }}',
      projectProperty: project.fields?.join(', ') || ''
    },
    {
      presentationKey: '{{ formats }}',
      projectProperty:
        ('formats' in project && project.formats?.join(', ')) || ''
    },
    {
      presentationKey: '{{ methods }}',
      projectProperty:
        ('methods' in project && project.methods?.join(', ')) || ''
    },
    {
      presentationKey: '{{ services }}',
      projectProperty:
        ('services' in project && project.services?.join(', ')) || ''
    },
    {
      presentationKey: '{{ project_lead }}',
      projectProperty: project.projectLead?.join(', ') || ''
    },
    {
      presentationKey: '{{ team }}',
      projectProperty: project.team?.join(', ') || ''
    },
    {
      presentationKey: '{{ description }}',
      projectProperty: project.description
        ? markdownToPlaintext(project.description).trim()
        : ''
    },
    {
      presentationKey: '{{ urls }}',
      projectProperty: project.urls?.map(({url}: ProjectURL) => url).join('\n')
    },
    {
      presentationKey: '{{ related_projects }}',
      projectProperty: relatedProjectsUrls?.join('\n')
    }
  ];
};

export default getContentMapping;
